import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { dayOf } from "@/client/lib/dates";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { ensureTenantData } from "@/client/lib/session";
import { tasksQuery } from "@/client/lib/tasks-query";
import { useCompleteTask } from "@/client/lib/use-complete-task";
import { useNow } from "@/client/lib/use-now";
import { peopleQuery } from "@/features/people/people-query";
import type { PersonRow } from "@/features/people/schemas";
import { ProjectPage } from "@/features/tasks/components/project-page";
import {
  ProjectGone,
  ProjectPending,
} from "@/features/tasks/components/project-states";
import { tasksCopy } from "@/features/tasks/copy";
import type { FilterContext } from "@/features/tasks/filter-options";
import {
  earlierTasksQuery,
  type ProjectTask,
  projectsQuery,
  projectTasks,
} from "@/features/tasks/projects-query";
import {
  VIEW_DEFAULTS,
  type ViewSearch,
  viewSearch,
} from "@/features/tasks/task-view";
import {
  useProjectTaskWrites,
  useProjectWrites,
} from "@/features/tasks/use-project-writes";
import { useTaskMoves } from "@/features/tasks/use-task-moves";
import { useReschedule } from "@/features/tasks/use-task-writes";

export const Route = createFileRoute(
  "/_authenticated/$slug/projects/$projectId",
)({
  validateSearch: viewSearch,
  search: { middlewares: [stripSearchParams(VIEW_DEFAULTS)] },
  // A project this person cannot see, or one deleted, reads as not there.
  loader: async ({ context, params }) => {
    const [projects] = await Promise.all([
      ensureTenantData(context.queryClient, projectsQuery),
      ensureTenantData(context.queryClient, peopleQuery),
    ]);
    const project = projects.find((one) => one.id === params.projectId);
    // Counts are null for an Admin outside a private project, who does not read its tasks.
    if (project && project.total !== null) {
      await Promise.all([
        ensureTenantData(context.queryClient, tasksQuery),
        ensureTenantData(
          context.queryClient,
          earlierTasksQuery(params.projectId, project.archivedAt !== null),
        ),
      ]);
    }
    return { name: project?.name };
  },
  head: ({ match, loaderData }) => ({
    meta: [
      {
        title: pageTitle(
          loaderData?.name ?? tasksCopy.projects.title,
          match.context.companyName,
        ),
      },
    ],
  }),
  pendingMs: PENDING_MS,
  pendingComponent: PendingProject,
  component: Project,
});

function Project() {
  const { slug, projectId } = Route.useParams();
  const { companyTimezone, companyName, email, role } = Route.useRouteContext();
  const { data: projects } = useSuspenseQuery(projectsQuery);
  const { data: people } = useSuspenseQuery(peopleQuery);
  const project = projects.find((one) => one.id === projectId);
  const reads = project !== undefined && project.total !== null;
  const { data: working = [] } = useQuery({ ...tasksQuery, enabled: reads });
  // Archiving changes the key, so the old rows stay until the new read answers. Another project's
  // rows never stand in: its key's second part is a different id.
  const { data: earlier = [] } = useQuery({
    ...earlierTasksQuery(projectId, Boolean(project?.archivedAt)),
    enabled: reads,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === projectId ? previous : undefined,
  });
  const tasks = projectTasks(working, earlier, projectId);
  const { edit, changePeople, archive, remove } = useProjectWrites();
  const meId = people.find((person) => person.email === email)?.id ?? null;
  const { add } = useProjectTaskWrites(projectId, meId ?? "");
  const { complete, uncheck, kept, leaving } = useCompleteTask();
  const { reorder } = useTaskMoves<ProjectTask>(tasksQuery.queryKey);
  const reschedule = useReschedule();
  const view = Route.useSearch();
  const navigate = Route.useNavigate();
  const onView = useViewChange();
  // Every minute, so "Today" and late dates turn over at midnight on the company's clock.
  const now = useNow(60_000);
  // Deleted, private to others, or deleted from another tab while open.
  if (!project) return <ProjectGone slug={slug} />;
  const today = dayOf(now, companyTimezone);
  // As the repository decides (`project-repository.ts`); it refuses anyone else on its own.
  const edits =
    role === "admin" ||
    (role === "manager" && project.leadId === meId) ||
    (meId !== null && project.people.includes(meId));

  return (
    <ProjectPage
      slug={slug}
      project={project}
      tasks={tasks}
      people={new Map(people.map((person) => [person.id, person]))}
      meId={meId}
      companyName={companyName ?? ""}
      timeZone={companyTimezone}
      today={today}
      onEdit={(patch) => edit({ id: project.id, patch })}
      onPeople={(personId, add) =>
        changePeople({ id: project.id, personId, add })
      }
      onCheck={(task, done) => (done ? complete(task) : uncheck(task))}
      onDueChange={reschedule}
      kept={kept}
      leaving={leaving}
      onAdd={add}
      edits={edits}
      onRename={(name) => edit({ id: project.id, patch: { name } })}
      onArchive={(archived) => archive({ id: project.id, archived })}
      onDelete={
        role === "admin"
          ? () => {
              remove(project);
              void navigate({ to: "/$slug/projects", params: { slug } });
            }
          : null
      }
      archivedOn={
        project.archivedAt
          ? dayOf(new Date(project.archivedAt), companyTimezone)
          : null
      }
      view={view}
      openTask={view.task}
      onView={onView}
      filterContext={filterContextOf(people, meId, today)}
      onMove={reorder}
    />
  );
}

/** The project's first load, with Filter and Display working on the address already. */
function PendingProject() {
  const { companyTimezone, email } = Route.useRouteContext();
  const people = useQueryClient().getQueryData(peopleQuery.queryKey) ?? [];
  const meId = people.find((person) => person.email === email)?.id ?? null;
  return (
    <ProjectPending
      view={Route.useSearch()}
      onView={useViewChange()}
      filterContext={filterContextOf(
        people,
        meId,
        dayOf(new Date(), companyTimezone),
      )}
    />
  );
}

function useViewChange() {
  const navigate = Route.useNavigate();
  return (change: Partial<ViewSearch>) =>
    navigate({
      search: (prev: ViewSearch) => ({ ...prev, ...change }),
      replace: true,
    });
}

/** What Filter offers: the active people, and who "me" and "today" are. */
function filterContextOf(
  people: readonly PersonRow[],
  meId: string | null,
  today: string,
): FilterContext {
  return {
    people: people.filter(
      (person) => person.kind === "person" && person.state === "active",
    ),
    meId: meId ?? "",
    today,
  };
}
